import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Package, CheckCircle, Inbox, Loader2, ShoppingBag, Info, RefreshCw, Search, X, AlertTriangle
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../../components/ui/alert-dialog';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCard = ({ product, onAccept, accepted }) => {
  const alreadyOwned = product.already_in_backshop && !accepted;
  return (
  <div
    className={`p-4 rounded-xl border-2 transition-all ${
      accepted
        ? 'border-green-300 bg-green-50/50 dark:bg-green-950/10 dark:border-green-800 opacity-70'
        : alreadyOwned
          ? 'border-blue-200 bg-blue-50/30 dark:bg-blue-950/10 dark:border-blue-800 opacity-80'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:shadow-md hover:border-primary/30'
    }`}
    data-testid={`suggestion-card-${product.id}`}
  >
    <div className="flex items-start gap-3">
      <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden border">
        {product.brand_media?.[product.brand]?.image_url ? (
          <img
            src={product.brand_media[product.brand].image_url}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <Package className="h-5 w-5 text-gray-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-sm leading-tight">{product.product_name}</h3>
          {accepted && (
            <Badge className="bg-green-600 text-white text-xs px-2 py-0.5">
              <CheckCircle className="h-3 w-3 mr-1 inline" />
              Übernommen
            </Badge>
          )}
          {alreadyOwned && (
            <Badge className="bg-blue-600 text-white text-xs px-2 py-0.5" data-testid={`already-owned-${product.id}`}>
              <CheckCircle className="h-3 w-3 mr-1 inline" />
              Bereits im Sortiment
            </Badge>
          )}
        </div>
        {product.ean && (
          <p className="text-xs font-mono text-muted-foreground mt-0.5">EAN: {product.ean}</p>
        )}
        {product.brand_numbers?.[product.brand] && (
          <p className="text-xs text-muted-foreground">
            Art.Nr.: <span className="font-mono">{product.brand_numbers[product.brand]}</span>
          </p>
        )}
        {product.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
        )}
      </div>
    </div>
    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800">
      <Button
        size="sm"
        className="w-full h-8 text-xs"
        disabled={accepted || alreadyOwned}
        variant={accepted || alreadyOwned ? 'secondary' : 'default'}
        onClick={() => !accepted && !alreadyOwned && onAccept(product)}
        data-testid={`accept-btn-${product.id}`}
      >
        {accepted ? (
          <>
            <CheckCircle className="h-3 w-3 mr-1" />
            In Backshop übernommen
          </>
        ) : alreadyOwned ? (
          <>
            <CheckCircle className="h-3 w-3 mr-1" />
            Bereits im Sortiment
          </>
        ) : (
          <>
            <ShoppingBag className="h-3 w-3 mr-1" />
            In meinen Backshop übernehmen
          </>
        )}
      </Button>
    </div>
  </div>
  );
};

export const BackshopVorschlaege = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acceptedIds, setAcceptedIds] = useState(new Set());
  const [accepting, setAccepting] = useState(false);
  const [activeLocationId, setActiveLocationId] = useState(null);

  // Suche + Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [sortimentFilter, setSortimentFilter] = useState('new'); // 'all' | 'new' | 'owned'
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Duplikat-Dialog
  const [duplicateDialog, setDuplicateDialog] = useState(null); // { productId, productName, existingId, existingName, imageUrl, pdfUrl }
  const [updatingMedia, setUpdatingMedia] = useState(false);

  // Fetch user's first location for brand-aware matching
  useEffect(() => {
    const fetchFirstLocation = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/admin/locations`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.length > 0) setActiveLocationId(res.data[0].id);
      } catch { /* silently ignore */ }
    };
    fetchFirstLocation();
  }, []);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const locationParam = activeLocationId ? `?location_id=${activeLocationId}` : '';
      const res = await axios.get(`${API}/admin/master-products/suggestions${locationParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data || []);
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error('Fehler beim Laden der Vorschläge');
      }
    } finally {
      setLoading(false);
    }
  }, [activeLocationId]);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  // ── Gefilterte & gesuchte Produkte ──────────────────────────────────────
  const filteredProducts = useMemo(() => {
    let list = products;
    // Sortiment-Filter
    if (sortimentFilter === 'new')   list = list.filter(p => !p.already_in_backshop && !acceptedIds.has(p.id));
    if (sortimentFilter === 'owned') list = list.filter(p => p.already_in_backshop || acceptedIds.has(p.id));
    // Textsuche
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(p => {
        const name = (p.product_name || '').toLowerCase();
        const artNr = ((p.brand_numbers || {})[p.brand] || '').toLowerCase();
        const ean = (p.ean || '').toLowerCase();
        return name.includes(q) || artNr.includes(q) || ean.includes(q);
      });
    }
    return list;
  }, [products, sortimentFilter, acceptedIds, searchQuery]);

  // Autocomplete-Vorschläge (max. 6, nur bei Tippen)
  const autocompleteSuggestions = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (q.length < 2) return [];
    return products
      .filter(p => {
        const name = (p.product_name || '').toLowerCase();
        const artNr = ((p.brand_numbers || {})[p.brand] || '').toLowerCase();
        return name.includes(q) || artNr.includes(q);
      })
      .slice(0, 6)
      .map(p => ({
        id: p.id,
        label: p.product_name,
        artNr: (p.brand_numbers || {})[p.brand] || '',
      }));
  }, [products, searchQuery]);
  // ────────────────────────────────────────────────────────────────────────

  const handleAccept = async (product) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/master-products/${product.id}/accept`,
        activeLocationId ? { location_id: activeLocationId } : {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.status === 'duplicate') {
        // Duplikat gefunden → Dialog anzeigen
        setDuplicateDialog({
          productId: product.id,
          productName: product.product_name,
          existingId: res.data.existing_product_id,
          existingName: res.data.existing_product_name,
          imageUrl: res.data.image_url,
          pdfUrl: res.data.pdf_url,
        });
        return;
      }

      setAcceptedIds(prev => new Set([...prev, product.id]));
      toast.success(`"${product.product_name}" wurde in Ihren Backshop übernommen!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Übernehmen');
    }
  };

  const handleConfirmUpdateMedia = async () => {
    if (!duplicateDialog) return;
    setUpdatingMedia(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/backshop-products/${duplicateDialog.existingId}/update-media`,
        { image_url: duplicateDialog.imageUrl, pdf_url: duplicateDialog.pdfUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAcceptedIds(prev => new Set([...prev, duplicateDialog.productId]));
      toast.success(`Bild & PDF für "${duplicateDialog.existingName}" wurden aktualisiert!`);
      setDuplicateDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Aktualisieren');
    } finally {
      setUpdatingMedia(false);
    }
  };

  const handleAcceptAll = async () => {
    if (products.length === 0) return;
    setAccepting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/master-products/accept-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allIds = new Set(products.map(p => p.id));
      setAcceptedIds(allIds);
      toast.success(`${res.data.accepted} Produkte in Ihren Backshop übernommen!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Übernehmen aller Produkte');
    } finally {
      setAccepting(false);
    }
  };

  const newCount = products.filter(p => !acceptedIds.has(p.id) && !p.already_in_backshop).length;
  const alreadyOwnedCount = products.filter(p => p.already_in_backshop).length;
  const hasBrand = !!user?.brand;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        {/* ── Header ───────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Inbox className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Produkt-Vorschläge
              </h1>
              <p className="text-sm text-muted-foreground">
                Genehmigte Produkte aus dem Master-Katalog für{' '}
                <span className="font-semibold text-foreground">{user?.brand || 'Ihre Marke'}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
          {newCount > 0 && (
            <Button
              onClick={handleAcceptAll}
              disabled={accepting}
              data-testid="accept-all-btn"
              className="shrink-0"
            >
              {accepting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ShoppingBag className="h-4 w-4 mr-2" />
              )}
              Alle {newCount} übernehmen
            </Button>
          )}
          </div>
        </div>

        {/* ── Suche + Toggle ─────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          {/* Autocomplete-Suche */}
          <div className="relative flex-1 max-w-sm" ref={searchRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              className="pl-9 pr-8"
              placeholder="Name oder Artikelnummer suchen…"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
              data-testid="product-search-input"
            />
            {searchQuery && (
              <button
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => { setSearchQuery(''); setShowSuggestions(false); }}
              ><X className="h-3.5 w-3.5" /></button>
            )}
            {/* Autocomplete Dropdown */}
            {showSuggestions && autocompleteSuggestions.length > 0 && (
              <div className="absolute z-50 top-full mt-1 w-full rounded-lg border bg-background shadow-lg overflow-hidden" data-testid="autocomplete-dropdown">
                {autocompleteSuggestions.map(s => (
                  <button
                    key={s.id}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-muted text-left"
                    onMouseDown={() => { setSearchQuery(s.label); setShowSuggestions(false); }}
                  >
                    <span className="font-medium">{s.label}</span>
                    {s.artNr && <span className="text-xs text-muted-foreground font-mono ml-2">{s.artNr}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Toggle: Alle / Neu / Im Sortiment */}
          <div className="flex rounded-lg border overflow-hidden shrink-0" data-testid="sortiment-toggle">
            {[
              { key: 'all',   label: `Alle (${products.length})` },
              { key: 'new',   label: `Neu (${products.filter(p => !p.already_in_backshop && !acceptedIds.has(p.id)).length})` },
              { key: 'owned', label: `Im Sortiment (${products.filter(p => p.already_in_backshop || acceptedIds.has(p.id)).length})` },
            ].map(opt => (
              <button
                key={opt.key}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  sortimentFilter === opt.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                }`}
                onClick={() => setSortimentFilter(opt.key)}
                data-testid={`filter-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Kein Marken-Setup */}
        {!hasBrand && (
          <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-800">
            <CardContent className="py-6 flex items-start gap-3">
              <Info className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-300">Kein Marken-Setup</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-0.5">
                  Ihrem Account ist noch keine Kraftstoffmarke zugewiesen. 
                  Bitte wenden Sie sich an den Administrator, um Ihre Marke einzurichten.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Leer */}
        {hasBrand && products.length === 0 && (
          <Card>
            <CardContent className="py-16 text-center">
              <Inbox className="h-14 w-14 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="font-semibold text-lg text-foreground">Keine Vorschläge vorhanden</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                Der Superadmin hat noch keine Produkte für Ihre Marke freigegeben.
                Legen Sie eigene Produkte an – diese werden automatisch als Vorschlag eingereicht.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Produkt-Grid */}
        {hasBrand && products.length > 0 && (
          <>
            {/* Status-Zeile */}
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant="outline" data-testid="suggestions-count">
                {filteredProducts.length} von {products.length} Vorschläge
              </Badge>
              {newCount > 0 && sortimentFilter !== 'owned' && (
                <Badge className="bg-primary/10 text-primary border-primary/20" variant="outline">
                  {newCount} noch nicht übernommen
                </Badge>
              )}
              {newCount === 0 && products.length > 0 && (
                <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800" variant="outline">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Alle übernommen
                </Badge>
              )}
            </div>

            {filteredProducts.length === 0 && searchQuery && (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Kein Produkt für „{searchQuery}" gefunden</p>
                <button className="text-xs text-primary underline mt-1" onClick={() => setSearchQuery('')}>Suche zurücksetzen</button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredProducts.map(p => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAccept={handleAccept}
                  accepted={acceptedIds.has(p.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── DUPLIKAT-DIALOG ───────────────────────────────────────────── */}
      <AlertDialog open={!!duplicateDialog} onOpenChange={(o) => { if (!o) setDuplicateDialog(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Produkt bereits vorhanden
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-left">
              <p>
                <span className="font-medium text-foreground">"{duplicateDialog?.existingName}"</span> befindet sich bereits in Ihrem Sortiment.
              </p>
              {(duplicateDialog?.imageUrl || duplicateDialog?.pdfUrl) ? (
                <p className="mt-2">
                  Möchten Sie <strong>Bild{duplicateDialog?.pdfUrl ? ' und PDF' : ''}</strong> aus dem Master-Katalog aktualisieren?
                </p>
              ) : (
                <p className="mt-2 text-muted-foreground">
                  Für dieses Produkt sind keine Medien im Master-Katalog hinterlegt.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDuplicateDialog(null)}>
              Abbrechen
            </AlertDialogCancel>
            {(duplicateDialog?.imageUrl || duplicateDialog?.pdfUrl) && (
              <AlertDialogAction
                onClick={handleConfirmUpdateMedia}
                disabled={updatingMedia}
                className="bg-primary"
                data-testid="confirm-update-media-btn"
              >
                {updatingMedia ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Bild & PDF aktualisieren
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};
